<template>
  <div
    class="video-container"
    @mouseover="showPauseButton"
    @mouseleave="hidePauseButton"
    :data-e2e="AppCypressAttribute.views.VIDEO_PLAYER"
  >
    <video ref="videoPlayer" class="video-js" :class="{ 'circle-video': isCircular }"></video>
    <template v-if="hasCustomControls">
      <transition name="fade">
        <div
          v-show="!isPlaying || isPauseButtonVisible"
          class="video-overlay"
          :data-e2e="AppCypressAttribute.views.VIDEO_OVERLAY"
        />
      </transition>
      <div
        class="play-button"
        @click="playVideo"
        v-show="!isPlaying && !isReplayButtonVisible"
        :data-e2e="AppCypressAttribute.buttons.VIDEO_PLAY"
      >
        <img :src="VideoPlayIllustration" alt="" />
      </div>
      <div
        class="pause-button"
        @click="togglePause"
        v-show="isPlaying && isPauseButtonVisible"
        :data-e2e="AppCypressAttribute.buttons.VIDEO_PAUSE"
      >
        <img :src="VideoPauseIllustration" alt="" />
      </div>
      <div
        class="replay-button"
        @click="replayVideo"
        v-show="isReplayButtonVisible"
        :data-e2e="AppCypressAttribute.buttons.VIDEO_REPLAY"
      >
        <img :src="VideoReplayIllustration" alt="" />
      </div>
    </template>
  </div>
</template>

<script>
import 'video.js/dist/video-js.css';
import { VideoType } from '@/modules/core/app/constants/VideoType';
import { HasContainerMixin } from '@/modules/core/app/mixins/HasContainerMixin';
import { VideoPlayerPresenter } from '@/modules/core/app/renderer/VideoPlayerPresenter';
import { AppCypressAttribute } from '@/modules/core/app/constants/AppCypressAttribute';
import { VideoPlayerTrackingService } from '@/modules/core/app/services/VideoPlayerTrackingService';
import VideoPlayIllustration from '@/assets/illustrations/video-play.svg';
import VideoPauseIllustration from '@/assets/illustrations/video-pause.svg';
import VideoReplayIllustration from '@/assets/illustrations/video-replay.svg';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'VideoPlayer',
  emits: ['on-play', 'on-ended', 'on-timeupdate'],
  mixins: [HasContainerMixin],
  props: {
    url: {
      type: String,
      required: true
    },
    type: {
      type: String,
      default: VideoType.MP4
    },
    isCircular: {
      type: Boolean,
      default: false
    },
    poster: {
      type: String,
      default: ''
    },
    segmentSeconds: {
      type: Number,
      default: 30
    },
    shouldTrackEvents: {
      type: Boolean,
      default: false
    },
    videoIdentifier: {
      type: String,
      default: ''
    },
    hasControls: {
      type: Boolean,
      default: false
    }
  },
  static: {
    AppCypressAttribute,
    VideoPlayIllustration,
    VideoPauseIllustration,
    VideoReplayIllustration
  },
  data() {
    return {
      presenter: null,
      isPlaying: false,
      isPauseButtonVisible: false,
      isReplayButtonVisible: false,
      trackingService: null
    };
  },
  computed: {
    hasCustomControls() {
      return !this.hasControls;
    }
  },
  methods: {
    playVideo() {
      this.presenter.play();
    },
    showPauseButton() {
      this.isPauseButtonVisible = true;
    },
    hidePauseButton() {
      this.isPauseButtonVisible = false;
    },
    replayVideo() {
      this.presenter.replayVideo();
      this.isReplayButtonVisible = false;
    },
    togglePause() {
      this.presenter.togglePause();
    },
    onPlay() {
      this.isPlaying = true;
      this.trackingService?.trackVideoStarted();
      this.$emit('on-play');
    },
    onEnded() {
      this.isReplayButtonVisible = true;
      this.trackingService?.trackVideoEnded();
      this.$emit('on-ended');
    },
    onPause() {
      this.isPlaying = false;
    },
    onTimeUpdate(player) {
      this.trackingService?.trackTimeUpdate(player.currentTime(), this.segmentSeconds);
      this.$emit('on-timeupdate', player);
    },
    init() {
      this.presenter.initialize();
      this.presenter.setPoster(this.poster);
      this.presenter.setControls(this.hasControls);
      this.presenter.setPauseListener(this.onPause);
      this.presenter.setEndedListener(this.onEnded);
      this.presenter.setPlayListener(this.onPlay);
      this.presenter.setTimeUpdateListener(this.onTimeUpdate);
    }
  },
  mounted() {
    this.presenter = this.$resolve(VideoPlayerPresenter, this, this.url, this.mediaType);

    if (this.shouldTrackEvents) {
      this.trackingService = this.$resolve(VideoPlayerTrackingService, this.videoIdentifier);
    }

    this.init();
  }
};
</script>

<style scoped>
.circle-video {
  clip-path: circle();
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.8s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
